/**
 * PlatformAdminsPage - list platform admins, add new ones, activate/deactivate
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { LoadingState, StatusBadge, ConfirmDialog, Toast } from '../components/common';
import { CreatePlatformAdminModal } from '../components/platform-admins/CreatePlatformAdminModal';
import { usePlatformAdminsPage } from '../hooks/usePlatformAdminsPage';
import { useAuth } from '../contexts/AuthContext';

export function PlatformAdminsPage() {
  const { admin: currentAdmin } = useAuth();
  const { query, modal, mutations, toast } = usePlatformAdminsPage();
  const [confirmTarget, setConfirmTarget] = useState<{
    id: string;
    email: string;
    nextStatus: 'active' | 'inactive';
  } | null>(null);

  if (query.hasError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-800 dark:text-red-200">Failed to load platform admins</p>
      </div>
    );
  }

  const activeCount = query.admins.filter((a) => a.status === 'active').length;

  return (
    <div className="space-y-6">
      <LoadingState isLoading={query.isLoading} message="Loading admins..." />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-subtitle text-gray-900 dark:text-white">Platform Admins</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            ResolveAI staff with access to this control plane
          </p>
        </div>
        <button
          onClick={modal.open}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Admin
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {query.admins.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No platform admins found
                  </td>
                </tr>
              )}
              {query.admins.map((admin) => {
                const isSelf = admin.id === currentAdmin?.id;
                const isLastActiveAdmin = admin.status === 'active' && activeCount <= 1;
                const disableDeactivate = admin.status === 'active' && (isSelf || isLastActiveAdmin);

                return (
                  <tr key={admin.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {admin.name ?? '—'}
                      {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{admin.email}</td>
                    <td className="px-6 py-4">
                      <StatusBadge status={admin.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() =>
                          setConfirmTarget({
                            id: admin.id,
                            email: admin.email,
                            nextStatus: admin.status === 'active' ? 'inactive' : 'active',
                          })
                        }
                        disabled={disableDeactivate}
                        title={
                          isSelf
                            ? "You can't deactivate your own account"
                            : isLastActiveAdmin
                              ? 'Cannot deactivate the last active admin'
                              : undefined
                        }
                        className={`text-sm font-medium ${
                          admin.status === 'active'
                            ? 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                            : 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300'
                        } disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-inherit`}
                      >
                        {admin.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CreatePlatformAdminModal
        isOpen={modal.isOpen}
        onClose={modal.close}
        onSubmit={(payload) => mutations.create.mutate(payload)}
        isSubmitting={mutations.create.isPending}
      />

      <ConfirmDialog
        isOpen={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => {
          if (confirmTarget) {
            mutations.updateStatus.mutate({ id: confirmTarget.id, status: confirmTarget.nextStatus });
          }
        }}
        title={confirmTarget?.nextStatus === 'inactive' ? 'Deactivate Admin' : 'Activate Admin'}
        message={
          confirmTarget?.nextStatus === 'inactive'
            ? `Are you sure you want to deactivate "${confirmTarget?.email}"? They will lose access to the control plane.`
            : `Are you sure you want to reactivate "${confirmTarget?.email}"?`
        }
        confirmLabel={confirmTarget?.nextStatus === 'inactive' ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.nextStatus === 'inactive' ? 'danger' : 'info'}
      />

      <Toast isOpen={toast.isOpen} onClose={toast.close} message={toast.message} variant={toast.variant} />
    </div>
  );
}
